import { generatePath, data, redirect, ShouldRevalidateFunctionArgs } from "react-router";
import { propagate404 } from "~/lib/api/response-helpers.server";
import { CollegeRelationships, EcosystemV2 } from "~/lib/api/ecosystemV2.server";
import { EcosystemV1 } from "~/lib/api/ecosystemV1.server";
import { RoutePaths } from "~/lib/routing";
import { leadFormSession } from "~/lib/sessions.server";
import { isAuthenticated } from "~/lib/auth0/userAuth.server";
import { CollegeData } from "~/components/pages/CollegePage";
import type { Route } from "./+types/CollegeProfileData";

export function shouldRevalidate({
    currentParams,
    nextParams,
    defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
    if (currentParams.collegeID === nextParams.collegeID) {
        return false;
    }
    return defaultShouldRevalidate;
}

export const meta: Route.MetaFunction = ({ data }) => {
    const { college } = data;
    return [{ title: college.attrs.name }];
};

export async function loader({ request, params }: Route.LoaderArgs) {
    const authenticated = await isAuthenticated(request);
    const [
        { data: college },
        {
            data: { result: sport },
        },
    ] = await Promise.all([
        EcosystemV2.GetCollege(params.collegeID).catch(propagate404()),
        EcosystemV1.GetSport(params.sportSlug),
    ]);

    if (college.data.attrs.slug !== params.collegeSlug) {
        const redirectPath = generatePath(RoutePaths.COLLEGE_PROFILE, {
            sportSlug: params.sportSlug,
            collegeID: params.collegeID,
            collegeSlug: college.data.attrs.slug,
        });
        throw redirect(redirectPath);
    }

    const relationships = new CollegeRelationships(college);
    const result = {
        college: college.data,
        majors: relationships.majors(),
        sport,
        sports: relationships.sports(),
    };

    const session = await leadFormSession(request);
    const sessionData = await session.get();
    await session.set({ ...sessionData, leadFormSourceURL: request.url });
    const headers = await session.headers();

    const sportId = sport.ref.split("/")[1];
    if (sportId === undefined) {
        return data(result as CollegeData, { headers });
    }
    const program = relationships.program(sportId);
    const rosterPromise = relationships.roster(program?.id);
    const coachesPromise = relationships.coaches(program?.id);

    return data(
        {
            ...result,
            coaches: coachesPromise,
            conference: relationships.conference(program),
            division: relationships.division(program),
            program,
            rosterData: rosterPromise,
            authenticated,
        },
        { headers },
    );
}
